import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { DropTarget } from 'react-dnd';
import _ from 'lodash';
import FormEditorField, { FORM_FIELD, DEFAULT_FIELD, RESERVED_FIELD_PLACEHOLDERS } from './FormEditorField';
import Button from './Button';
import { formTarget,
         collectTarget } from '../../../client/dnd.js';


const countDuplicateNames = (name, fields, disallowedPlaceholderNames = undefined, fieldName = '$Name$') => {
    let count = fields.filter(field => field[fieldName] === name).length;
    if (disallowedPlaceholderNames) {
        count += disallowedPlaceholderNames.filter(field => field === name).length;
    }

    return count;
};


const validateFormField = (field, fields) => {
    if (RESERVED_FIELD_PLACEHOLDERS.includes(field['$Name$']) && field['$New$']) {
        field.touched = true;
        field.error = {
            'name' : 'The name is reserved, please use a different name.'
        };

        return false;
    }

    if (countDuplicateNames(field['$Name$'], fields) > 1 && field['$New$']) {
        field.touched = true;
        field.error = {
            'name': 'The name is taken, please use a different one.'
        };

        return false;
    }

    if (field['$Name$'] === '') {
        field.touched = true;
        field.error = {
            'name': 'Cannot be empty.'
        };

        return false;
    }

    delete field['touched'];
    delete field['error'];
    return true;
};


const validateFormFields = (fields) => {
    let noError = true;
    for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        noError = noError && validateFormField(field, fields);
    }

    return noError;
};

export const prepareFieldsForSaving = (fields, prefix = '') => {
    for (let i = 0; i < fields.length; i++) {
        let field = fields[i];
        if (field['$Name$']) {
            field.name = field['$Name$'];
        }
        
        if (field.name.startsWith(prefix)) {
            field.name = field.name.substring(prefix.length);
        }

        delete field['$Name$'];
        delete field['$Editing$'];
        delete field['$Id$'];
        delete field['$New$'];
        fields[i] = field;
    }
};


const getFieldId = (field, props) => {
    let id = field.name;
    if (id && !id.startsWith(props.placeholderNamePrefix)) {
        id = props.placeholderNamePrefix + id;
    }

    return id;
};


const enhanceFields = (props) => {
    let fields;
    if (props && props.fields) {
        fields = [];
        fields = _.cloneDeep(props.fields);
        for (let i = 0; i < fields.length; i++) {
            let field = fields[i];
            if (!field['$Id$']) {
                field['$Id$'] = getFieldId(field, props);
                let index = 0;
                if (props.placeholderNamePrefix && props.placeholderNamePrefix.length > 0) {
                    index = props.placeholderNamePrefix.length;
                }

                field['$Name$'] = field.name.substring(index);
            }

            fields[i] = field;
        }
    }

    return fields;
};

class FormEditor extends PureComponent {
    constructor(props) {
        super(props);
        this.onDelete = this.onDelete.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onAdd = this.onAdd.bind(this);
        this.onChange = this.onChange.bind(this);
        this.renderEditor = this.renderEditor.bind(this);
        this.moveField = this.moveField.bind(this);
        this.findField = this.findField.bind(this);
        this.collapseFields = this.collapseFields.bind(this);
        this.generatePlaceholderName = this.generatePlaceholderName.bind(this);
    }

    onDelete(field) {
        let fields = _.cloneDeep(this.props.fields);
        let index = this.findField(field['$Id$']).index;
        fields.splice(index, 1);
        validateFormFields(fields);
        this.props.onChange(fields);
    }

    onChange(field) {
        let fields = _.cloneDeep(this.props.fields);
        let index = this.findField(field['$Id$']).index;
        const labelChanged = fields[index].props.label !== field.props.label;
        fields[index] = field;
        if (field['$New$'] && labelChanged) {
            field['$Name$'] = this.generatePlaceholderName(field.props.label, index);
        }

        validateFormField(field, fields);
        this.props.onChange(fields);
    }

    onSave(props) {
        let fields = _.cloneDeep(props.fields);
        prepareFieldsForSaving(fields);
        if (this.props.onSave) {
            this.props.onSave(fields);
        }
    }

    onAdd(currentField = undefined) {
        let field = _.cloneDeep(DEFAULT_FIELD);
        field.name = this.generatePlaceholderName('New', -1, true);
        field.props.label = '[CHANGE ME]';
        field['$Id$'] = getFieldId(field, this.props);
        field['$Name$'] = field.name;
        field['$New$'] = true;
        let fields = _.cloneDeep(this.props.fields || []);
        if (currentField === undefined) {
            fields.push(field);
        } else {
            const index = this.props.fields.findIndex(f => f['$Id$'] === currentField['$Id$']);
            fields.splice(index + 1, 0, field);
        }

        this.props.onChange(fields);
    }

    generatePlaceholderName(base, ignoreField = -1, ignoreId = false) {
        if (!base) {
            base = 'New';
        }

        base = _.upperFirst(_.camelCase(base));
        let fields = [...this.props.fields];
        if (ignoreField >= 0) {
            fields.splice(ignoreField, 1);
        }

        let placeholder = base;
        let i = 1;
        do {
            if (i > 1) {
                placeholder = base + i;
            }

            i++;

        } while (countDuplicateNames(placeholder, fields) > 0 ||
                 (ignoreId && fields.filter(field => field['$Id$'] === this.props.placeholderNamePrefix + placeholder).length > 0) ||
                 countDuplicateNames(placeholder, fields, this.props.disallowedPlaceholderNames, 'name') > 0);

        return placeholder;
    }

    findField(id) {
        let index = this.props.fields.findIndex(field => {
            return field['$Id$'] == id;
        });

        if (index < 0) {
            return {
                field: {},
                index: -1
            }
        }

        let field = _.cloneDeep(this.props.fields[index]);
        return {
            field,
            index
        };
    }

    moveField(draggedId, targetIndex) {
        const { field, index: sourceIndex } = this.findField(draggedId);
        let fields = [...this.props.fields];
        fields.splice(sourceIndex, 1);
        fields.splice(targetIndex, 0, field);
        this.props.onChange(fields);
    }

    collapseFields(field) {
        let fields = [...this.props.fields];
        fields = fields.map(f => {
            return { 
                ...f,
                '$Editing$':  field && f['$Id$'] === field['$Id$'] ? field['$Editing$'] : false,
            }
        });

        this.props.onChange(fields);
    }

    renderEditor() {
        const {
            connectDropTarget,
            renderSaveButton = true,
            renderCancelButton = true,
            disabled = false,
            visibility = false,
            suggestedFields = []
        } = this.props;

        if (!this.props.fields) {
            return (<div/>);
        }

        const errorIndex = this.props.fields.findIndex(field => typeof(field.error) !== 'undefined');
        const saveDisabled = errorIndex >= 0;
        const fieldsFiltered = this.props.fields.filter(field => ((field.type !== 'Hidden') && (field.type !== 'CaseType') && (field.type !== 'ClientType')));
        const fieldNames = this.props.fields.map(f => f.name);
        const generatedFields = fieldsFiltered.map((field) => {
            return (
                <FormEditorField initialField={field}
                                 disallowedFieldTypes={this.props.disallowedFieldTypes}
                                 onDelete={this.onDelete}
                                 onChange={this.onChange}
                                 onAdd={this.onAdd}
                                 key={field['$Id$']}
                                 id={field['$Id$']}
                                 options={this.props.options}
                                 findField={this.findField}
                                 moveField={this.moveField}
                                 collapseFields={this.collapseFields}
                                 prefix={this.props.placeholderNamePrefix}
                                 disabled={disabled}
                                 visibility={visibility}
                                 suggestedFields={suggestedFields.filter(f => !fieldNames.includes(f.name))}
                />
            );
        });

        let isLastFieldEdited = false;
        if (fieldsFiltered && fieldsFiltered.length > 0 && fieldsFiltered[fieldsFiltered.length - 1]['$Editing$']) {
            isLastFieldEdited = true;
        }

        return connectDropTarget(
            <div>
            <Row>
                <Col xs={12}>
                    {generatedFields}
                </Col>
            </Row>
            {!disabled && !isLastFieldEdited && (
            <Row>
                <Col xs={12}>
                    <Button onClick={() => this.onAdd()}
                            block
                            bsStyle='link'
                            icon="plus"
                            iconStyle={{paddingBottom: '6px'}}
                            label="Add Field"
                            tooltip="Add new field"
                            tooltipPosition="bottom"/>
                </Col>
            </Row>)}
            <Row>
                <Col xs={2}></Col>
                {renderCancelButton && (
                <Col xs={4}>
                    <Button onClick={this.props.onClose}
                            block
                            icon="remove"
                            label="Cancel"/>
                </Col>)}
                {renderSaveButton && (
                <Col xs={4}>
                    <Button onClick={this.onSave}
                            disabled={saveDisabled}
                            bsStyle="primary"
                            icon="floppy-disk"
                            block
                            label="Save"/>
                </Col>)}
            </Row>
            </div>
        );
    }

    render() {
        return this.renderEditor();
    }
}

FormEditor.propTypes = {
    fields: PropTypes.array.isRequired,
    suggestedFields: PropTypes.array,
    onClose: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    renderSaveButton: PropTypes.bool,
    renderCancelButton: PropTypes.bool,
    options: PropTypes.object,
    disabled: PropTypes.bool,
    visibility: PropTypes.array,
    placeholderNamePrefix: PropTypes.string,
    disallowedPlaceholderNames: PropTypes.array,

    //React DnD:
    connectDropTarget: PropTypes.func.isRequired
};

const mapStateToProps = (state, ownProps) => {
    const fields = enhanceFields(ownProps);
    return { fields };
};

export default connect(mapStateToProps)(DropTarget(FORM_FIELD, formTarget, collectTarget)(FormEditor));